import { Globe, Smartphone, Palette, Brain, Wrench, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { services } from '../data/mock';

const iconMap = {
  Globe: Globe,
  Smartphone: Smartphone,
  Palette: Palette,
  Brain: Brain,
  Wrench: Wrench
};

const colorMap = {
  0: { gradient: 'from-[#3B82F6] to-[#60A5FA]', bg: 'bg-[#3B82F6]/10', border: 'border-[#3B82F6]/30', hover: 'hover:border-[#3B82F6]/60', icon: 'text-[#3B82F6]' },
  1: { gradient: 'from-[#8B5CF6] to-[#A78BFA]', bg: 'bg-[#8B5CF6]/10', border: 'border-[#8B5CF6]/30', hover: 'hover:border-[#8B5CF6]/60', icon: 'text-[#8B5CF6]' },
  2: { gradient: 'from-[#F97316] to-[#FB923C]', bg: 'bg-[#F97316]/10', border: 'border-[#F97316]/30', hover: 'hover:border-[#F97316]/60', icon: 'text-[#F97316]' },
  3: { gradient: 'from-[#10B981] to-[#34D399]', bg: 'bg-[#10B981]/10', border: 'border-[#10B981]/30', hover: 'hover:border-[#10B981]/60', icon: 'text-[#10B981]' },
  4: { gradient: 'from-[#EC4899] to-[#F472B6]', bg: 'bg-[#EC4899]/10', border: 'border-[#EC4899]/30', hover: 'hover:border-[#EC4899]/60', icon: 'text-[#EC4899]' },
};

const serviceIdMap = {
  "Website Development": "website-development",
  "App Development": "app-development",
  "Design & Branding": "uiux-design",
  "Agentic AI Solutions": "ai-solutions",
  "Business Tools": "business-tools"
};

const Services = () => {
  const navigate = useNavigate();

  const handleViewService = (serviceName) => {
    const serviceId = serviceIdMap[serviceName];
    if (serviceId) {
      navigate(`/services/${serviceId}`);
    }
  };

  return (
    <section id="services" className="relative py-24 md:py-32" style={{ background: 'var(--bg-card)' }}>
      {/* Gradient Top Border */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#3B82F6]/50 to-transparent"></div>
      
      {/* Background Accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#8B5CF6]/5 rounded-full blur-[200px]"></div>
      
      <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-full mb-6">
            <span className="text-[#8B5CF6] text-sm font-semibold">Our Services</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight" style={{ letterSpacing: '-0.02em' }}>
            Comprehensive{' '}
            <span className="bg-gradient-to-r from-[#3B82F6] via-[#8B5CF6] to-[#F97316] bg-clip-text text-transparent">
              Digital Solutions
            </span>
          </h2>
          
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            From concept to deployment, we deliver end-to-end solutions that drive business growth and innovation.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            const colors = colorMap[index % 5];
            return (
              <div 
                key={service.id}
                data-testid={`service-card-${index}`}
                className={`group relative bg-[#0A0A0F]/80 backdrop-blur-sm border ${colors.border} ${colors.hover} rounded-2xl p-8 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-black/20 cursor-pointer`}
                onClick={() => handleViewService(service.title)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Hover Glow */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${colors.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                
                <div className="relative">
                  {/* Icon */}
                  <div className={`${colors.bg} w-14 h-14 rounded-xl flex items-center justify-center mb-6 border ${colors.border} group-hover:scale-110 transition-transform duration-300`}>
                    <Icon size={26} className={colors.icon} />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-white transition-colors duration-400">
                    {service.title}
                  </h3>
                  
                  <p className="text-white/60 leading-relaxed mb-6 text-sm">
                    {service.description}
                  </p>

                  {/* Learn More Link */}
                  <div className={`flex items-center gap-2 ${colors.icon} group-hover:gap-4 transition-all duration-300`}>
                    <span className="text-sm font-semibold">View Packages</span>
                    <ArrowRight size={16} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-white/50 mb-6">Need a custom solution tailored to your business?</p>
          <button 
            data-testid="services-cta"
            onClick={() => navigate('/signup')}
            className="inline-flex items-center gap-3 px-8 py-4 text-lg font-semibold text-white rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#3B82F6]/25"
            style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
          >
            Get Started
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
